import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Skeleton from "../components/Skeleton";
import {
  getBroadcastNotifications,
  sendBroadcastNotification,
} from "../services/notificationService";
import { getAuthToken } from "../services/apiClient";

const initialFormData = {
  title: "",
  message: "",
  image: null,
};

const normalizeNotifications = (response) => {
  const payload = response?.data ?? response;

  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.notifications)) return payload.notifications;
  if (Array.isArray(payload?.broadcasts)) return payload.broadcasts;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.rows)) return payload.rows;

  return [];
};

const formatDate = (value) => {
  if (!value) return "Just now";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "Just now";

  return new Intl.DateTimeFormat("en", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const getNotificationId = (notification, index) =>
  notification.id ||
  notification._id ||
  notification.notificationId ||
  `${notification.title || "notification"}-${index}`;

const getNotificationImage = (notification) =>
  notification.image ||
  notification.imageUrl ||
  notification.image_url ||
  notification.banner ||
  notification.mediaUrl ||
  notification.media_url;

const formatFileSize = (size) => {
  if (!size) return "";

  if (size < 1024 * 1024) {
    return `${Math.max(1, Math.round(size / 1024))} KB`;
  }

  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const Notifications = () => {
  const [formData, setFormData] = useState(initialFormData);
  const [notifications, setNotifications] = useState([]);
  const [errors, setErrors] = useState({});
  const [statusMessage, setStatusMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [imagePreviewUrl, setImagePreviewUrl] = useState("");
  const imageInputRef = useRef(null);

  const selectedImageName = useMemo(
    () => formData.image?.name || "Choose image",
    [formData.image]
  );

  const hasAuthToken = Boolean(getAuthToken());

  const fetchNotifications = useCallback(async () => {
    try {
      setIsLoading(true);
      setStatusMessage("");

      const response = await getBroadcastNotifications();
      setNotifications(normalizeNotifications(response));
    } catch (error) {
      setStatusMessage(
        error.message || "Unable to load notifications right now."
      );
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  useEffect(() => {
    const handlePageAction = (event) => {
      if (event.detail?.path === "/notifications" && event.detail?.action === "refresh") {
        fetchNotifications();
      }
    };

    window.addEventListener("admin-page-action", handlePageAction);
    return () => window.removeEventListener("admin-page-action", handlePageAction);
  }, [fetchNotifications]);

  useEffect(() => {
    if (!formData.image) {
      setImagePreviewUrl("");
      return undefined;
    }

    const previewUrl = URL.createObjectURL(formData.image);
    setImagePreviewUrl(previewUrl);

    return () => URL.revokeObjectURL(previewUrl);
  }, [formData.image]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));

    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: "",
    }));
  };

  const handleImageChange = (event) => {
    const image = event.target.files?.[0] || null;

    if (image && !image.type.startsWith("image/")) {
      setFormData((prevData) => ({
        ...prevData,
        image: null,
      }));
      setErrors((prevErrors) => ({
        ...prevErrors,
        image: "Please upload a valid image file",
      }));
      event.target.value = "";
      return;
    }

    setFormData((prevData) => ({
      ...prevData,
      image,
    }));

    setErrors((prevErrors) => ({
      ...prevErrors,
      image: "",
    }));
  };

  const handleRemoveImage = () => {
    setFormData((prevData) => ({
      ...prevData,
      image: null,
    }));

    setErrors((prevErrors) => ({
      ...prevErrors,
      image: "",
    }));

    if (imageInputRef.current) {
      imageInputRef.current.value = "";
    }
  };

  const validateForm = () => {
    const nextErrors = {};

    if (!formData.title.trim()) {
      nextErrors.title = "Subject is required";
    }

    if (!formData.message.trim()) {
      nextErrors.message = "Message is required";
    }

    if (formData.image && !formData.image.type.startsWith("image/")) {
      nextErrors.image = "Please upload a valid image file";
    }

    setErrors(nextErrors);

    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) return;

    try {
      setIsSubmitting(true);
      setStatusMessage("");

      await sendBroadcastNotification({
        title: formData.title.trim(),
        message: formData.message.trim(),
        image: formData.image,
      });

      setFormData(initialFormData);
      event.target.reset();
      setImagePreviewUrl("");
      await fetchNotifications();
      setStatusMessage("Notification sent successfully.");
    } catch (error) {
      setStatusMessage(error.message || "Unable to send notification.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="main-wrapper">
      <div className="page-wrapper">
        <div className="page-content">
          <div className="row">
            <div className="col-12">
              <div className="card rounded-4">
                <div className="row p-4 g-4">
                  <div className="col-lg-4">
                    {!hasAuthToken && (
                      <div className="alert alert-warning" role="alert">
                        Authorization token is missing. Add authToken in
                        localStorage or set REACT_APP_API_TOKEN.
                      </div>
                    )}

                    {statusMessage && (
                      <div className="alert alert-info" role="alert">
                        {statusMessage}
                      </div>
                    )}

                    <form
                      className="request-order-form"
                      onSubmit={handleSubmit}
                    >
                      <div className="row g-3 mb-3">
                        <div className="col-lg-12">
                          <label htmlFor="notificationTitle" className="form-label">
                            Subject
                          </label>
                          <input
                            type="text"
                            className={`form-control ${
                              errors.title ? "is-invalid" : ""
                            }`}
                            id="notificationTitle"
                            name="title"
                            value={formData.title}
                            onChange={handleChange}
                            placeholder="Enter notification subject"
                          />
                          {errors.title && (
                            <div className="invalid-feedback d-block">
                              {errors.title}
                            </div>
                          )}
                        </div>

                        <div className="col-lg-12">
                          <label
                            htmlFor="notificationMessage"
                            className="form-label"
                          >
                            Message
                          </label>
                          <textarea
                            className={`form-control ${
                              errors.message ? "is-invalid" : ""
                            }`}
                            id="notificationMessage"
                            name="message"
                            value={formData.message}
                            onChange={handleChange}
                            placeholder="Enter message"
                            rows="5"
                            style={{ resize: "none" }}
                          />
                          {errors.message && (
                            <div className="invalid-feedback d-block">
                              {errors.message}
                            </div>
                          )}
                        </div>

                        <div className="col-lg-12">
                          <div className="form-upload-section">
                            <label
                              className={`upload-box notification-upload-box ${
                                imagePreviewUrl ? "has-preview" : ""
                              }`}
                              htmlFor="imageUpload"
                            >
                              {imagePreviewUrl ? (
                                <img
                                  src={imagePreviewUrl}
                                  alt="Selected notification"
                                  className="notification-image-preview"
                                />
                              ) : (
                                <>
                                  <div className="upload-icon"></div>
                                  <div className="upload-text">
                                    <h3>{selectedImageName}</h3>
                                    <p>JPG, PNG, WEBP or GIF</p>
                                  </div>
                                </>
                              )}
                            </label>
                            <input
                              type="file"
                              id="imageUpload"
                              accept="image/*"
                              ref={imageInputRef}
                              onChange={handleImageChange}
                            />
                          </div>
                          {formData.image && (
                            <div className="notification-image-meta">
                              <div>
                                <strong>{formData.image.name}</strong>
                                <span>{formatFileSize(formData.image.size)}</span>
                              </div>
                              <button
                                type="button"
                                className="link-text"
                                onClick={handleRemoveImage}
                              >
                                Remove
                              </button>
                            </div>
                          )}
                          {errors.image && (
                            <div className="invalid-feedback d-block">
                              {errors.image}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="col-12">
                        <button
                          type="submit"
                          className="btn btn-primary"
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? "Sending..." : "Send"}
                        </button>
                      </div>
                    </form>
                  </div>

                  <div className="col-lg-8">
                    <div className="responisve-table">
                      <table width="100%" border="0" cellSpacing="0" cellPadding="0">
                        <thead>
                          <tr>
                            <th>S.No.</th>
                            <th>All Notifications</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {isLoading && (
                            <>
                              {[1, 2, 3].map((item) => (
                                <tr key={item}>
                                  <td colSpan="3" className="py-4">
                                    <Skeleton width="100%" height="1.5rem" />
                                  </td>
                                </tr>
                              ))}
                            </>
                          )}

                          {!isLoading && notifications.length === 0 && (
                            <tr>
                              <td colSpan="3" className="text-center py-4">
                                No notifications found.
                              </td>
                            </tr>
                          )}

                          {!isLoading &&
                            notifications.map((notification, index) => {
                              const notificationId = getNotificationId(
                                notification,
                                index
                              );
                              const title = notification.title || notification.subject || "Untitled notification";
                              const message = notification.message || notification.body || notification.description || "";
                              const createdAt =
                                notification.createdAt ||
                                notification.created_at ||
                                notification.updatedAt;
                              const imageUrl = getNotificationImage(notification);

                              return (
                                <tr key={notificationId}>
                                  <td>{index + 1}.</td>
                                  <td>
                                    <div className="notification-list">
                                      <div className="row p-0 align-items-center justify-lg-content-between activeity-list-inner">
                                        <div className="col-lg-9">
                                          <div className="d-flex align-items-center gap-3">
                                            {imageUrl && (
                                              <img
                                                src={imageUrl}
                                                alt=""
                                                className="notification-list-image"
                                              />
                                            )}
                                            <div>
                                              <p>{title}</p>
                                              <div className="time-duration d-flex align-items-center gap-2">
                                                {message}
                                              </div>
                                            </div>
                                          </div>
                                        </div>

                                        <div className="col-lg-3 text-end">
                                          <span className="border-0 text-dark">
                                            {formatDate(createdAt)}
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                  </td>
                                  <td><span className="text-muted">Sent</span></td>
                                </tr>
                              );
                            })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Notifications;
